import React from 'react';
import { individualCommentarySchema } from '../../schemas/socketEvents';

/**
 * CommentaryValidationBoundary - Error boundary and validation wrapper for commentary events
 * 
 * This component serves as a protective layer around commentary rendering to:
 * 1. Validate incoming commentary events against the unified schema
 * 2. Handle validation errors gracefully without breaking the UI
 * 3. Provide fallback rendering for malformed events
 * 4. Log validation issues for monitoring
 * 
 * Part of Task 23: Unified Commentary System implementation
 */
class CommentaryValidationBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorInfo: null,
      validationErrors: []
    };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to monitoring service
    console.error('[CommentaryValidationBoundary] Rendering error caught:', error, errorInfo);
    this.setState({
      errorInfo: errorInfo
    });
  }

  /**
   * Validates a commentary event against the unified schema
   * @param {object} event - Commentary event to validate
   * @returns {object} - { isValid: boolean, validatedEvent: object, errors: array }
   */
  validateCommentaryEvent(event) {
    try {
      // First validate against the full schema
      const result = individualCommentarySchema.safeParse(event);
      
      if (result.success) {
        return {
          isValid: true,
          validatedEvent: result.data,
          errors: []
        };
      }

      // If validation fails, try to fix common issues
      const fixedEvent = this.attemptEventRepair(event);
      const retryResult = individualCommentarySchema.safeParse(fixedEvent);
      
      if (retryResult.success) {
        console.warn('[CommentaryValidationBoundary] Event repaired and validated:', {
          original: event,
          fixed: fixedEvent
        });
        return {
          isValid: true,
          validatedEvent: retryResult.data,
          errors: ['Event required repair']
        };
      }

      // Log validation failures for monitoring
      console.error('[CommentaryValidationBoundary] Validation failed:', {
        event,
        errors: result.error.format()
      });

      return {
        isValid: false,
        validatedEvent: this.createFallbackEvent(event),
        errors: result.error.issues.map(issue => issue.message)
      };
    } catch (error) {
      console.error('[CommentaryValidationBoundary] Validation error:', error);
      return {
        isValid: false,
        validatedEvent: this.createFallbackEvent(event),
        errors: [error.message]
      };
    }
  }

  /**
   * Attempts to repair common event format issues
   * @param {object} event - Malformed event
   * @returns {object} - Repaired event
   */
  attemptEventRepair(event) {
    const repaired = { ...event };

    // Ensure required fields exist
    if (!repaired.event_type && repaired.type) {
      repaired.event_type = repaired.type;
    }

    if (!repaired.message_text && repaired.message) {
      repaired.message_text = repaired.message;
    }

    if (typeof repaired.minute !== 'number' && repaired.time) {
      repaired.minute = parseInt(repaired.time, 10) || 0;
    }

    if (!repaired.formal_minute && repaired.minute) {
      repaired.formal_minute = String(repaired.minute);
    }

    // Fix narrative structure if present
    if (repaired.narrative) {
      // Ensure narrative has proper structure
      if (typeof repaired.narrative === 'string') {
        repaired.narrative = {
          action: repaired.narrative,
          buildup: '',
          outcome: '',
          aftermath: '',
          descriptors: []
        };
      }

      // Ensure descriptors is an array
      if (repaired.narrative.descriptors && !Array.isArray(repaired.narrative.descriptors)) {
        repaired.narrative.descriptors = [];
      }

      // Trim narrative fields to max lengths
      if (repaired.narrative.buildup && repaired.narrative.buildup.length > 200) {
        repaired.narrative.buildup = repaired.narrative.buildup.substring(0, 197) + '...';
      }
      if (repaired.narrative.action && repaired.narrative.action.length > 300) {
        repaired.narrative.action = repaired.narrative.action.substring(0, 297) + '...';
      }
      if (repaired.narrative.outcome && repaired.narrative.outcome.length > 200) {
        repaired.narrative.outcome = repaired.narrative.outcome.substring(0, 197) + '...';
      }
      if (repaired.narrative.aftermath && repaired.narrative.aftermath.length > 200) {
        repaired.narrative.aftermath = repaired.narrative.aftermath.substring(0, 197) + '...';
      }

      // Limit descriptors array
      if (repaired.narrative.descriptors && repaired.narrative.descriptors.length > 10) {
        repaired.narrative.descriptors = repaired.narrative.descriptors.slice(0, 10);
      }
    }

    // Add schema version if missing
    if (!repaired.schema_version) {
      repaired.schema_version = '1.0';
    }

    return repaired;
  }

  /**
   * Creates a fallback event for display when validation fails completely
   * @param {object} event - Invalid event
   * @returns {object} - Fallback event safe for rendering
   */
  createFallbackEvent(event) {
    return {
      event_type: event.event_type || event.type || 'SYSTEM_MESSAGE',
      minute: parseInt(event.minute, 10) || 0,
      formal_minute: String(event.minute || 0),
      message_text: event.message_text || event.message || 'Commentary data unavailable',
      team_context: null,
      player_name: null,
      secondary_player_name: null,
      icon: null,
      emphasis: [],
      narrative: null,
      schema_version: '1.0',
      _validationFallback: true
    };
  }

  /**
   * Validates a batch of commentary events
   * @param {array} events - Array of commentary events
   * @returns {array} - Array of validated events
   */
  validateCommentaryBatch(events) {
    if (!Array.isArray(events)) {
      console.error('[CommentaryValidationBoundary] Expected array of events, received:', typeof events);
      return [];
    }

    const validatedEvents = [];
    const validationErrors = [];

    events.forEach((event, index) => {
      const validation = this.validateCommentaryEvent(event);
      validatedEvents.push(validation.validatedEvent);
      
      if (!validation.isValid) {
        validationErrors.push({
          index,
          event: event,
          errors: validation.errors
        });
      }
    });

    // Log batch validation summary
    if (validationErrors.length > 0) {
      console.warn('[CommentaryValidationBoundary] Batch validation issues:', {
        total: events.length,
        failed: validationErrors.length,
        errors: validationErrors
      });
    }

    return validatedEvents;
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI when there's a rendering error
      return (
        <div className="commentary-error-boundary">
          <div style={{
            padding: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            margin: '10px',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#c00', marginBottom: '10px' }}>Commentary Display Error</h3>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              There was an error displaying the match commentary.
            </p>
            <button 
              onClick={() => {
                this.setState({ hasError: false, errorInfo: null });
                window.location.reload();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#c00',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reload Commentary
            </button>
          </div>
        </div>
      );
    }

    // Pass validation function to children
    const childrenWithValidation = React.Children.map(this.props.children, child => {
      if (React.isValidElement(child)) {
        // Avoid injecting props into DOM nodes/fragments (causes React warnings).
        if (typeof child.type === 'string' || child.type === React.Fragment) {
          return child;
        }
        return React.cloneElement(child, {
          validateCommentaryEvent: this.validateCommentaryEvent.bind(this),
          validateCommentaryBatch: this.validateCommentaryBatch.bind(this)
        });
      }
      return child;
    });

    return <>{childrenWithValidation}</>;
  }
}

export default CommentaryValidationBoundary;
